// @ts-nocheck
import React, { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Upload, Calendar, DollarSign, Percent, Hash,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ───────────────────────────────────────────────────────────────────── *
 * Types
 * ───────────────────────────────────────────────────────────────────── */
interface Question {
  id: string;
  question_text: string;
  question_type: string;
  required?: boolean;
  pillar?: string;
  weight?: number;
  help_text?: string;
  options?: string[];
  min_value?: number;
  max_value?: number;
  conditional_logic?: {
    depends_on_question_id?: string;
    show_if_answer?: string;
  };
  data_category?: string;
  data_mapping?: string;
  is_system?: boolean;
  sub_items_config?: {
    items?: Array<{ id: string; label: string }>;
  };
}

interface FormRendererProps {
  /** The form object (title, description, questions, etc.) */
  form: {
    title?: string;
    description?: string;
    questions?: Question[];
    calculation_groups?: any[];
    thank_you_message?: string;
  };
  /** Called when the user submits the form */
  onSubmit?: (answers: Record<string, any>) => void | Promise<void>;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
  /** If true, fields are read-only (for viewing past submissions) */
  readOnly?: boolean;
  /** Pre-populated answers (e.g. for viewing a submission) */
  initialAnswers?: Record<string, any>;
  /** Number of questions per page (0 = all on one page) */
  pageSize?: number;
  /** Custom submit button label */
  submitLabel?: string;
  /** If provided, navigation/submit buttons are rendered via this callback instead of inline */
  renderFooter?: (footerProps: FormFooterProps) => React.ReactNode;
}

export interface FormFooterProps {
  currentPage: number;
  totalPages: number;
  isLastPage: boolean;
  isSubmitting: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  submitLabel: string;
  progress: number;
  readOnly: boolean;
  submitted: boolean;
}

/* ───────────────────────────────────────────────────────────────────── *
 * Helpers
 * ───────────────────────────────────────────────────────────────────── */
function isQuestionVisible(question: Question, answers: Record<string, any>): boolean {
  const logic = question.conditional_logic;
  if (!logic?.depends_on_question_id) return true;
  const parentAnswer = answers[logic.depends_on_question_id];
  if (parentAnswer === undefined || parentAnswer === null || parentAnswer === "") return false;
  if (logic.show_if_answer) {
    return String(parentAnswer).toLowerCase() === String(logic.show_if_answer).toLowerCase();
  }
  return true;
}

function validateField(question: Question, value: any): string | null {
  if (!question.required) return null;
  if (value === undefined || value === null || value === "") {
    return `${question.question_text} is required`;
  }
  if (question.question_type === "multiple_choice" && Array.isArray(value) && value.length === 0) {
    return `${question.question_text} is required`;
  }
  return null;
}

function formatCurrency(value: string): string {
  const num = value.replace(/[^0-9.]/g, "");
  if (!num) return "";
  const parts = num.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

/* ───────────────────────────────────────────────────────────────────── *
 * Scale Selector (1-10)
 * ───────────────────────────────────────────────────────────────────── */
function ScaleSelector({ value, onChange, disabled, min = 1, max = 10 }: {
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
}) {
  const range = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="flex flex-wrap gap-2">
      {range.map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n)}
          className={cn(
            "w-10 h-10 rounded-lg border-2 text-sm font-semibold transition-all",
            value === n
              ? "bg-indigo-600 border-indigo-600 text-white shadow-md scale-110"
              : "bg-white border-slate-200 text-slate-600 hover:border-indigo-400 hover:bg-indigo-50",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── *
 * Single question renderer
 * ───────────────────────────────────────────────────────────────────── */
function QuestionField({ question, value, onChange, error, disabled }: {
  question: Question;
  value: any;
  onChange: (v: any) => void;
  error?: string | null;
  disabled?: boolean;
}) {
  const renderField = () => {
    switch (question.question_type) {
      case "scale":
        return (
          <ScaleSelector
            value={value ?? null}
            onChange={onChange}
            disabled={disabled}
            min={question.min_value ?? 1}
            max={question.max_value ?? 10}
          />
        );

      case "yes_no":
        return (
          <RadioGroup value={value ?? ""} onValueChange={onChange} disabled={disabled} className="flex gap-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`} className="cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`} className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        );

      case "single_choice":
        return (
          <RadioGroup value={value ?? ""} onValueChange={onChange} disabled={disabled} className="space-y-2">
            {(question.options || []).map((opt, i) => (
              <div key={i} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-50">
                <RadioGroupItem value={opt} id={`${question.id}-opt-${i}`} />
                <Label htmlFor={`${question.id}-opt-${i}`} className="cursor-pointer flex-1">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "multiple_choice": {
        const selected: string[] = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(question.options || []).map((opt, i) => (
              <div key={i} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-50">
                <Checkbox
                  id={`${question.id}-opt-${i}`}
                  checked={selected.includes(opt)}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selected, opt] : selected.filter((s) => s !== opt));
                  }}
                />
                <Label htmlFor={`${question.id}-opt-${i}`} className="cursor-pointer flex-1">{opt}</Label>
              </div>
            ))}
          </div>
        );
      }

      case "text":
        return (
          <Input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer…"
            disabled={disabled}
          />
        );

      case "long_text":
        return (
          <Textarea
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer…"
            rows={4}
            disabled={disabled}
          />
        );

      case "number":
        return (
          <div className="relative max-w-xs">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="number"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="0"
              disabled={disabled}
              className="pl-9"
            />
          </div>
        );

      case "currency":
        return (
          <div className="relative max-w-xs">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={value ? formatCurrency(String(value)) : ""}
              onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="0.00"
              disabled={disabled}
              className="pl-9"
            />
          </div>
        );

      case "percentage":
        return (
          <div className="relative max-w-xs">
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="number"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="0"
              disabled={disabled}
              className="pr-9"
              min={0}
              max={100}
            />
          </div>
        );

      case "date":
        return (
          <div className="relative max-w-xs">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="pl-9"
            />
          </div>
        );

      case "file_upload":
        return (
          <div className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
            disabled ? "border-slate-200 bg-slate-50" : "border-slate-300 hover:border-indigo-400 cursor-pointer",
          )}>
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">
              {value ? `Selected: ${value}` : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG up to 10MB</p>
            {!disabled && (
              <Input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => onChange(e.target.files?.[0]?.name || "")}
              />
            )}
          </div>
        );

      case "data_entry": {
        const subItems = question.sub_items_config?.items || [];
        const entries = value && typeof value === "object" ? value : {};
        return (
          <div className="space-y-3">
            {subItems.length > 0 ? (
              subItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <Label className="w-40 text-sm text-slate-700 shrink-0">{item.label}</Label>
                  <div className="relative flex-1 max-w-xs">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={entries[item.id] ? formatCurrency(String(entries[item.id])) : ""}
                      onChange={(e) => onChange({ ...entries, [item.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="0.00"
                      disabled={disabled}
                      className="pl-9"
                    />
                  </div>
                </div>
              ))
            ) : (
              <Input
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter value…"
                disabled={disabled}
              />
            )}
          </div>
        );
      }

      case "calculated":
        return (
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-500 italic">This field is auto-calculated</p>
            {value != null && <p className="text-lg font-semibold text-slate-900 mt-1">{value}</p>}
          </div>
        );

      default:
        return (
          <Input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer…"
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className={cn("space-y-2", error && "animate-in fade-in-0 duration-300")}>
      <div className="flex items-start gap-2">
        <Label className="text-base font-medium text-slate-900 leading-snug">
          {question.question_text}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
      {question.help_text && (
        <p className="text-sm text-slate-500">{question.help_text}</p>
      )}
      {renderField()}
      {error && (
        <div className="flex items-center gap-1.5 text-sm text-red-600">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── *
 * Main FormRenderer
 * ───────────────────────────────────────────────────────────────────── */
export default function FormRenderer({
  form,
  onSubmit,
  isSubmitting = false,
  readOnly = false,
  initialAnswers = {},
  pageSize = 0,
  submitLabel = "Submit",
  renderFooter,
}: FormRendererProps) {
  const [answers, setAnswers] = useState<Record<string, any>>(initialAnswers);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const questions = form.questions || [];

  // Filter visible questions (conditional logic)
  const visibleQuestions = useMemo(
    () => questions.filter((q) => isQuestionVisible(q, answers)),
    [questions, answers],
  );

  // Pagination
  const totalPages = pageSize > 0 ? Math.ceil(visibleQuestions.length / pageSize) : 1;
  const pageQuestions = pageSize > 0
    ? visibleQuestions.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    : visibleQuestions;

  const progress = useMemo(() => {
    if (visibleQuestions.length === 0) return 0;
    const answered = visibleQuestions.filter((q) => {
      const v = answers[q.id];
      if (v === undefined || v === null || v === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }).length;
    return Math.round((answered / visibleQuestions.length) * 100);
  }, [visibleQuestions, answers]);

  const handleChange = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    // Clear error on change
    setErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  const validatePage = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    const toValidate = pageSize > 0 ? pageQuestions : visibleQuestions;
    toValidate.forEach((q) => {
      const err = validateField(q, answers[q.id]);
      if (err) errs[q.id] = err;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [pageQuestions, visibleQuestions, answers, pageSize]);

  const validateAll = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    visibleQuestions.forEach((q) => {
      const err = validateField(q, answers[q.id]);
      if (err) errs[q.id] = err;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [visibleQuestions, answers]);

  const handleNext = () => {
    if (validatePage()) {
      setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
    }
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 0));

  const handleSubmit = async () => {
    if (!validateAll()) {
      // Jump to the first page with an error
      if (pageSize > 0) {
        const firstErrorId = Object.keys(errors)[0] || visibleQuestions.find((q) => validateField(q, answers[q.id]))?.id;
        if (firstErrorId) {
          const idx = visibleQuestions.findIndex((q) => q.id === firstErrorId);
          if (idx >= 0) setCurrentPage(Math.floor(idx / pageSize));
        }
      }
      return;
    }
    try {
      await onSubmit?.(answers);
      setSubmitted(true);
    } catch {
      // errors should be handled by the parent
    }
  };

  // ── Submitted state ──
  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          {form.thank_you_message || "Form submitted successfully!"}
        </h3>
        <p className="text-slate-500">Your responses have been recorded.</p>
      </div>
    );
  }

  // ── No questions state ──
  if (questions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">This form has no questions.</p>
      </div>
    );
  }

  const isLastPage = currentPage >= totalPages - 1;

  const footerProps: FormFooterProps = {
    currentPage,
    totalPages,
    isLastPage,
    isSubmitting,
    onPrev: handlePrev,
    onNext: handleNext,
    onSubmit: handleSubmit,
    submitLabel,
    progress,
    readOnly,
    submitted,
  };

  return (
    <div className="space-y-6">
      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">{progress}% complete</span>
          {totalPages > 1 && (
            <span className="text-slate-500">
              Page {currentPage + 1} of {totalPages}
            </span>
          )}
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {pageQuestions.map((q) => (
          <QuestionField
            key={q.id}
            question={q}
            value={answers[q.id]}
            onChange={(v) => handleChange(q.id, v)}
            error={errors[q.id]}
            disabled={readOnly || isSubmitting}
          />
        ))}
      </div>

      {/* Inline Navigation / Submit (when no external renderFooter) */}
      {!renderFooter && !readOnly && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div>
            {totalPages > 1 && currentPage > 0 && (
              <Button type="button" variant="outline" onClick={handlePrev} disabled={isSubmitting}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {totalPages > 1 && !isLastPage ? (
              <Button type="button" onClick={handleNext} disabled={isSubmitting}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                {isSubmitting ? "Submitting…" : submitLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* External footer rendered by parent (e.g. fixed in drawer) */}
      {renderFooter && !submitted && renderFooter(footerProps)}
    </div>
  );
}
